import { QrCodeBlock } from '@/components/qr/QrCodeBlock';
import { generatePasscode } from '@/lib/passcode';
import { Button, FormFieldGroup, Input } from '@startupquickstart/frontend';
import { Dices } from 'lucide-react';
import type { ChallengeEditorSectionProps } from '../types';
import { SectionCard } from './SectionCard';

/**
 * Optional passcode lock: code + generate, the mobile prompt message, and a
 * printable QR for the code.
 */
export function AccessCodeSection({
  values,
  updateField,
  context,
}: ChallengeEditorSectionProps) {
  const code = values.accessCode.trim();

  return (
    <SectionCard
      id="accessCode"
      title="Access Code"
      description={
        context === 'library'
          ? 'Optional passcode to unlock this challenge. Leave blank for none. Copied into each event, where it can be overridden.'
          : 'Optional typed code that gates this challenge until a team enters it on mobile. Leave blank to disable.'
      }
    >
      <FormFieldGroup
        name="accessCode"
        label="Code"
        description="Case-insensitive. Type your own or generate a random one."
      >
        <div className="flex gap-2">
          <Input
            id="accessCode"
            value={values.accessCode}
            onChange={(e) => updateField('accessCode', e.target.value)}
            placeholder="e.g. MUSEUM26"
            autoComplete="off"
          />
          <Button
            type="button"
            variant="outline"
            onClick={() => updateField('accessCode', generatePasscode())}
          >
            <Dices className="h-4 w-4" />
            Generate
          </Button>
        </div>
      </FormFieldGroup>

      {code && (
        <>
          <FormFieldGroup
            name="accessCodeMessage"
            label="Prompt message"
            description="Hint shown above the code input on mobile."
          >
            <Input
              id="accessCodeMessage"
              value={values.accessCodeMessage}
              onChange={(e) => updateField('accessCodeMessage', e.target.value)}
              placeholder="e.g. Find the facilitator and ask for today's code."
            />
          </FormFieldGroup>

          <div className="flex flex-col items-center gap-2 rounded-md border bg-muted/20 p-4">
            <span className="font-mono text-xs uppercase tracking-wider text-muted-foreground">
              Access Code QR
            </span>
            <QrCodeBlock
              value={code}
              label={values.name || 'Access code'}
              size={160}
              alt={`QR for access code ${code}`}
            />
            <p className="text-center text-xs text-muted-foreground">
              Print and place at the challenge. Teams scan it to auto-fill the code.
              Remember to save — the QR encodes the code shown above.
            </p>
          </div>
        </>
      )}
    </SectionCard>
  );
}
